import {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {LocalOfferIcon} from '@ui/icons/material/LocalOffer';
import {offerLabel, useReferralOffer} from '@app/referrals/referral-offer';
import type {Cart} from './checkout-queries';

/*
 * The partner discount in a cart (checkout page and cart drawer): the row,
 * taking it off / putting it back, and the partner-code box.
 */

async function setReferral(on: boolean) {
  const r = on
    ? await apiClient.post<{cart: Cart}>('cart/referral')
    : await apiClient.delete<{cart: Cart}>('cart/referral');
  queryClient.setQueryData(['cart'], r.data.cart);
}

/**
 * The partner discount in the order summary, and why it is missing when the
 * student came through a partner but this order does not qualify.
 */
export function ReferralSummaryRows({cart, money}: {cart: Cart; money: (c: number) => string}) {
  const offer = useReferralOffer();
  const r = cart.referral;
  const amount = cart.referral_discount ?? 0;

  // Taken off by the student: say so, and offer it back.
  if (r?.removed) {
    return (
      <div className="flex items-center justify-between gap-8 rounded-lg bg-alt px-10 py-8 text-xs text-muted">
        <span><Trans message="Partner discount removed from this order." /></span>
        <button type="button" className="font-semibold text-primary hover:underline" onClick={() => setReferral(true)}>
          <Trans message="Apply it again" />
        </button>
      </div>
    );
  }

  if (amount > 0 && r) {
    return (
      <div className="rounded-lg bg-positive/10 px-10 py-8 text-positive">
        <div className="flex items-center justify-between gap-8">
          <span className="flex items-center gap-6 font-semibold">
            <LocalOfferIcon size="xs" />
            {offer ? (
              <Trans message="Partner discount :value" values={{value: offerLabel(offer)}} />
            ) : (
              <Trans message="Partner discount" />
            )}
          </span>
          <span dir="ltr" className="font-bold">−{money(amount)}</span>
        </div>
        {/* Why it is here without a code: the account was referred by a
            partner (a link opened earlier) — and it can be taken off. */}
        <div className="mt-2 flex items-center justify-between gap-8 text-2xs text-muted">
          <span>
            {r.partner_name ? (
              <Trans message="You joined through :partner, so this is applied automatically." values={{partner: r.partner_name}} />
            ) : (
              <Trans message="You joined through a partner, so this is applied automatically." />
            )}
          </span>
          {!r.preview && (
            <button type="button" className="shrink-0 font-semibold text-danger hover:underline" onClick={() => setReferral(false)}>
              <Trans message="Remove" />
            </button>
          )}
        </div>
        {r.preview && (
          <p className="mt-2 text-2xs text-muted">
            <Trans message="Confirmed when you sign in to pay." />
          </p>
        )}
      </div>
    );
  }

  if (r?.partner_id && r.reason && offer?.discount_available) {
    return (
      <p className="rounded-lg bg-warning/10 px-10 py-8 text-xs text-warning">
        {r.reason.includes('minimum') ? (
          <Trans
            message="Add :amount more to get your :value partner discount."
            values={{amount: money(Math.max(0, Math.round(offer.min_order * 100) - cart.subtotal)), value: offerLabel(offer)}}
          />
        ) : (
          <Trans message={r.reason} />
        )}
      </p>
    );
  }

  return null;
}

export function PartnerCodeBox({collapsible = false}: {collapsible?: boolean}) {
  const [open, setOpen] = useState(!collapsible);
  const {isLoggedIn} = useAuth();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<{ok: boolean; text: string} | null>(null);
  const apply = useMutation({
    mutationFn: (value: string) =>
      isLoggedIn
        ? apiClient
            .post<{claimed: boolean; code_applied: boolean; switched?: boolean; partner_name?: string}>('referral/claim', {code: value})
            .then(r => r.data)
        : // A guest has no account to attach the code to yet. It is recorded
          // like opening the partner's link; the claim happens at sign-in.
          apiClient
            .post<{tracked: boolean; partner_name?: string; offer?: any}>('referral/track', {code: value, landing_path: '/checkout'})
            .then(r => {
              queryClient.setQueryData(['referral-offer'], {offer: r.data.offer ?? null});
              try {
                localStorage.setItem('partner-ref', JSON.stringify(value));
              } catch {
                /* the cookie carries it */
              }
              return {claimed: r.data.tracked, code_applied: r.data.tracked && !!r.data.offer, switched: false, partner_name: r.data.partner_name};
            }),
    onSuccess: data => {
      if (data.code_applied) {
        setResult({ok: true, text: data.switched ? 'Partner code changed.' : 'Partner code applied.'});
        setCode('');
        // Entering a code means they want it: undo an earlier "remove".
        if (isLoggedIn) setReferral(true).catch(() => undefined);
        // A new referral can change the partner discount in the totals.
        queryClient.invalidateQueries({queryKey: ['cart']});
        queryClient.invalidateQueries({queryKey: ['referral-notice']});
      } else if (data.claimed) {
        setResult({ok: false, text: 'This code could not be used for your account.'});
      } else {
        setResult({ok: false, text: 'That partner code was not recognised.'});
      }
    },
    onError: () => setResult({ok: false, text: 'Could not apply that code. Please try again.'}),
  });

  if (!open) {
    return (
      <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setOpen(true)}>
        <Trans message="Have a different partner code? Change it" />
      </button>
    );
  }

  return (
    <div>
      <label className="mb-4 block text-xs font-medium text-muted">
        <Trans message="Partner code (optional)" />
      </label>
      <div className="flex gap-6">
        <input
          value={code}
          onChange={e => {
            setCode(e.target.value);
            setResult(null);
          }}
          placeholder="CODE"
          dir="ltr"
          className="h-36 min-w-0 flex-1 rounded-lg border bg-alt px-10 text-sm uppercase"
        />
        <Button
          variant="outline"
          size="sm"
          disabled={!code.trim() || apply.isPending}
          onClick={() => apply.mutate(code.trim())}
        >
          <Trans message="Apply" />
        </Button>
      </div>
      {result && (
        <p className={`mt-6 text-xs ${result.ok ? 'text-positive' : 'text-danger'}`}>
          <Trans message={result.text} />
        </p>
      )}
    </div>
  );
}
